/* eslint-disable react-hooks/exhaustive-deps */
import CloseIcon from "@mui/icons-material/Close";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import InfoIcon from "@mui/icons-material/Info";
import MedicationIcon from "@mui/icons-material/Medication";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FormikErrors, useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { FileInput } from "../../../components/FileInput/FileInput";
import { RppsAutocompleteByNameInput } from "../../../components/inputs/RppsAutocompleteByNameInput";
import Questions from "../../../components/Questions";
import {
  CreateAnswerDto,
  CreateAppointmentRequestDto,
  RppsDto,
  Site,
} from "../../../interfaces/api-interfaces";
import { AppointmentRequestService } from "../../../services/appointment-request.service";
import { ExamService } from "../../../services/exam.service";
import { QuestionService } from "../../../services/question.service";
import { SiteService } from "../../../services/sites.service";
import {
  ScintiEstPatientFormSchema,
  ScintiEstPatientFormValues,
} from "../../../shared/schemas/scinti-est-apointment-form.schema";
import { isValidDate } from "../../../utils";
import AppointmentSuccess from "./AppointmentSuccess";
import DateInput from "../../../components/inputs/DateInput";

// Compute age from a DD/MM/YYYY string without ever constructing a Date from
// the birthdate (which would introduce TZ shifts). Returns null for invalid input.
const computeAgeFromDDMMYYYY = (s: string): number | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s || "");
  if (!m) return null;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (!isValidDate(dd, mm, yyyy)) return null;
  const now = new Date();
  let age = now.getFullYear() - yyyy;
  const monthDiff = now.getMonth() + 1 - mm;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dd)) age -= 1;
  return age;
};

const ScintiEstPatientForm = () => {
  const siteService = useRef(new SiteService()).current;
  const examService = useRef(new ExamService()).current;
  const questionService = useRef(new QuestionService()).current;
  const [files, setFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const answersMap = useRef(new Map<number, CreateAnswerDto>()).current;

  const handleSubmit = async (values: ScintiEstPatientFormValues) => {
    if (values.referringDoctor === null) {
      return;
    }

    // Backend has no DTO field for personne-de-confiance phone or pregnancy
    // answers — fold them into comments above the user's free text.
    const userComments = values.comments ?? "";
    const lines: string[] = [];
    if (values.personneDeConfiancePhone) {
      lines.push(
        `Tél personne de confiance : ${values.personneDeConfiancePhone}`,
      );
    }
    if (values.pregnancyStatus) {
      lines.push(`Pensez-vous être enceinte : ${values.pregnancyStatus}`);
    }
    if (
      values.pregnancyStatus === "non" &&
      values.contraceptionOrSterility
    ) {
      lines.push(
        `Contraception / stérilité : ${values.contraceptionOrSterility}`,
      );
    }
    if (values.breastfeeding) {
      lines.push(`Allaitement : ${values.breastfeeding}`);
    }
    const prefix = lines.join("\n");
    const finalComments = prefix ? `${prefix}\n\n${userComments}` : userComments;

    const dto: CreateAppointmentRequestDto = {
      examId: parseInt(values.exam + ""),
      patientFirstName: values.patientFirstName,
      patientlastName: values.patientLastName,
      patientBirthDate: values.patientBirthDate.replace(/\//g, "-"),
      patientEmail: values.patientEmail,
      patientSex: values.patientSex === "male" ? "M" : "F",
      patientPhone: values.patientPhone,
      patientWeight: values.patientWeight ?? undefined,
      siteId: parseInt(values.site + ""),
      submittedBy: "patient",
      answers: Array.from(answersMap.values()),
      patientAddress: values.patientAddress,
      patientBirthPlace: values.patientBirthPlace,
      patientSsn: values.ssn ?? undefined,
      patientMobility: values.patientMobility,
      resultViaEmail: true,
      patientHeight: values.patientHeight ?? undefined,
      // Period dropdown removed — disponibilités now live in the comments field.
      preferredDate: "voir commentaires",
      referringDoctorEmail: "",
      referringDoctor: values.referringDoctor,
      files,
      comments: finalComments,
      foreignPatient: values.foreignPatient,
    };

    const appointmentRequestService = new AppointmentRequestService();
    try {
      setIsSending(true);
      if (errorMessage) {
        setErrorMessage("");
      }
      await appointmentRequestService.create(dto);
      setIsSuccess(true);
    } catch (err) {
      setErrorMessage(
        "Il y a eu un problème, merci de réessayer plus tard ou de nous contacter.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const formik = useFormik<ScintiEstPatientFormValues>({
    initialValues: {
      patientBirthPlace: "",
      patientSex: "",
      ssn: "",
      patientFirstName: "",
      patientLastName: "",
      patientPhone: "",
      patientEmail: "",
      patientBirthDate: "",
      examType: "",
      preferedDate: "voir commentaires",
      doctorRpps: "",
      comments: "",
      resultViaEmail: true,
      exam: -1,
      patientHeight: 0,
      patientWeight: 0,
      site: -1,
      patientAddress: "",
      referringDoctor: null,
      patientMobility: "",
      foreignPatient: false,
      personneDeConfiancePhone: "",
      pregnancyStatus: "",
      contraceptionOrSterility: "",
      breastfeeding: "",
    },
    validationSchema: ScintiEstPatientFormSchema,
    onSubmit: handleSubmit,
    validateOnChange: false,
    validateOnBlur: true,
  });

  const clientId = 4;
  const { data: sites } = useQuery<Site[]>(
    "sites",
    () => siteService.find(clientId),
    {
      initialData: [],
    },
  );

  const { data: examTypes } = useQuery({
    queryKey: ["examTypes", formik.values.site],
    queryFn: ({ queryKey }) => examService.findExamTypes(queryKey[1] as number),
    initialData: [],
  });

  const { data: exams } = useQuery({
    queryKey: ["exams", formik.values.site, formik.values.examType],
    queryFn: ({ queryKey }) => {
      const siteId = isNaN(queryKey[1] as number)
        ? undefined
        : (queryKey[1] as number);
      const examTypeId = isNaN(queryKey[2] as number)
        ? undefined
        : (queryKey[2] as number);

      return examService.findExams(siteId, examTypeId);
    },
    initialData: { totalCounts: 0, datas: [] },
  });

  const { data: questions } = useQuery({
    queryKey: ["questions", formik.values.exam],
    queryFn: ({ queryKey }) => questionService.find(queryKey[1] as number),
    initialData: [],
    enabled: !!formik.values.exam,
  });

  const siteOptions = sites?.map((site) => ({
    label: site.label,
    value: site.id,
  })) as { label: string; value: string | number }[];

  const examTypeOptions = examTypes?.map((type) => ({
    label: type.label,
    value: type.id.toString(),
  })) as { label: string; value: string }[];

  const examOptions = exams?.datas?.map((ex) => ({
    label: ex.label,
    value: ex.id.toString(),
  })) as { label: string; value: string }[];

  const handleRppsChange = (rpps: RppsDto | null) => {
    const { setFieldValue } = formik;
    setFieldValue("referringDoctor", {
      doctorName: rpps?.firstName + " " + rpps?.lastName,
      rpps: rpps?.rppsNumber,
      id: rpps?.id,
      skill: rpps?.skillLabel,
    });

    setFieldValue("doctorRpps", rpps?.rppsNumber);
  };
  const handleFileChange = (file: File) => {
    setFiles([...files, file]);
  };
  const handleFileDelete = (file: File) => {
    setFiles(files.filter((f) => f !== file));
  };

  const handleAnswerChange = (answerDto: CreateAnswerDto) => {
    const key = answerDto.questionId;
    answersMap.set(key, answerDto);
  };

  const scrollToFirstError = (
    errors: FormikErrors<ScintiEstPatientFormValues>,
  ) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField) return;

    const inputEl = document.querySelector(
      `[name="${firstErrorField}"]`,
    ) as HTMLElement | null;
    const targetEl = inputEl ?? document.getElementById(firstErrorField);

    if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      targetEl.focus?.();
    }
  };

  const onSubmit = async () => {
    const errors = await formik.validateForm();

    // Ordonnance is mandatory.
    if (files.length === 0) {
      setErrorMessage(
        "Au moins un document (ordonnance) doit être joint pour pouvoir soumettre la demande.",
      );
      return;
    }

    if (Object.values(errors).length !== 0) {
      setErrorMessage(
        "Certains champs obligatoires ne sont pas renseignés ou sont incorrects, merci de vérifier et soumettre à nouveau",
      );
      scrollToFirstError(errors);
      return;
    }
    await formik.submitForm();
  };

  useEffect(() => {
    answersMap.clear();
  }, [formik.values.exam]);

  // Derive the pregnancy block visibility purely from string inputs (no Date construction).
  const patientAge = computeAgeFromDDMMYYYY(formik.values.patientBirthDate);
  const showPregnancyBlock =
    formik.values.patientSex === "female" &&
    patientAge !== null &&
    patientAge >= 11 &&
    patientAge <= 50;

  const confirmationMessage = (
    <div>
      <p>
        Merci de confirmer votre demande en cliquant sur le lien envoyé sur
        votre boite mail. Si vous ne le trouvez pas, pensez à vérifier vos
        SPAMS.
        <br />
        <br />
        Vous pouvez ajouter l'adresse email suivante à vos contacts pour vous
        assurer de la bonne réception de ces emails dans le futur:
        <br />
        <br />
        <b>{process.env.REACT_APP_CONTACT_EMAIL}</b>
      </p>
    </div>
  );

  if (isSuccess) {
    return <AppointmentSuccess message={confirmationMessage} />;
  }

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      sx={{
        padding: "10px",
        backgroundColor: "#6EC1E410",
      }}
    >
      <Grid
        container
        xs={12}
        sx={{ height: "100%", padding: "20px", overflow: "auto" }}
      >
        {/* WELCOME + ACKNOWLEDGMENT GATE */}
        <Grid item xs={12} sx={{ marginBottom: "10px" }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{ color: "#0B6796", fontWeight: 600, marginBottom: "10px" }}
          >
            Bienvenue sur le service de demande de rendez-vous en Médecine
            Nucléaire.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: "8px" }}>
            Ce formulaire vous permet <b>uniquement</b> de déposer une demande
            de rendez-vous.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: "12px" }}>
            Après étude de votre dossier, notre service vous contactera par
            téléphone pour vous proposer une date adaptée.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: "8px" }}>
            Merci de prendre connaissance des points suivants avant d'envoyer
            votre demande&nbsp;:
          </Typography>
          <ul style={{ paddingLeft: "20px", marginTop: 0 }}>
            <li style={{ marginBottom: "6px" }}>
              <b>Ordonnance obligatoire</b>&nbsp;: nous ne pouvons planifier un
              rendez-vous sans ordonnance. Merci d'indiquer{" "}
              <b>l'intitulé exact</b> de l'examen inscrit sur celle-ci.
            </li>
            <li style={{ marginBottom: "6px" }}>
              <b>Vos coordonnées</b>&nbsp;: pensez à renseigner une adresse
              e-mail valide et surtout un numéro de téléphone sur lequel nous
              pourrons facilement vous joindre, ou le numéro de votre personne
              de confiance.
            </li>
            <li style={{ marginBottom: "6px" }}>
              <b>Femmes enceintes</b>&nbsp;: seules les scintigraphies
              pulmonaires peuvent être réalisées.
            </li>
            <li style={{ marginBottom: "6px" }}>
              <b>Poids et taille obligatoires</b>&nbsp;: ces informations nous
              permettent de préparer le bon dosage du produit injectable,
              commandé directement par notre service.
            </li>
            <li style={{ marginBottom: "6px" }}>
              <b>Documents complémentaires</b>&nbsp;: pour une prise en charge
              optimale, n'hésitez pas à joindre le maximum de documents
              demandés (ils varient selon l'examen prescrit).
            </li>
            <li style={{ marginBottom: "6px" }}>
              <b>Scintigraphie pulmonaire</b>&nbsp;: en raison du caractère
              potentiellement urgent de cet examen, la prise de rendez-vous
              est possible exclusivement par téléphone.
            </li>
          </ul>
          <Typography variant="body1" sx={{ marginTop: "8px" }}>
            Si vous avez besoin d'aide ou souhaitez échanger directement avec
            nous, notre standard est à votre écoute&nbsp;:
          </Typography>
          <ul style={{ paddingLeft: "20px", marginTop: 0 }}>
            <li>
              BESANCON Clinique St Vincent&nbsp;: <b>03 81 48 26 14</b>
            </li>
            <li>
              CHAUMONT&nbsp;: <b>03 25 30 71 57</b>
            </li>
            <li>
              VESOUL&nbsp;: <b>03 84 96 29 80</b>
            </li>
          </ul>
          <Typography variant="body1" sx={{ marginTop: "8px" }}>
            Nous vous invitons à enregistrer le numéro de votre centre afin
            d'identifier facilement notre appel.
          </Typography>
          <FormControlLabel
            sx={{ marginTop: "16px" }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(ev) => setAcknowledged(ev.target.checked)}
                sx={{
                  color: "#6EC1E4",
                  "&.Mui-checked": { color: "#0B6796" },
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Je prends en compte ces indications
              </Typography>
            }
          />
        </Grid>

        {acknowledged && (
          <>
            <Grid item xs={12} sx={{ marginBottom: "5px" }}>
              <Typography
                variant="body1"
                component="h6"
                sx={{ color: "orange", fontWeight: "500" }}
              >
                <InfoIcon sx={{ color: "orange", marginRight: "2px" }} />
                Les champs marqués d'une * sont obligatoires
              </Typography>
            </Grid>

            {/* DOCTEUR */}
            <Grid item xs={12}>
              <Divider>
                <ContactEmergencyIcon
                  sx={{ marginBottom: "-10px", color: "#6EC1E4" }}
                />
                <Typography
                  variant="h6"
                  component="h6"
                  sx={{ color: "#6EC1E4" }}
                >
                  Docteur
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Médecin qui demande l'examen (Prénom puis Nom)*
              </Typography>
              <Grid item xs={12}>
                <Typography style={{ color: "grey", fontSize: "12px" }}>
                  Merci de ne sélectionner qu'un seul résultat (vous pouvez
                  affiner la recherche avec le № départment).
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <RppsAutocompleteByNameInput onChange={handleRppsChange} />
                <Typography color="error">
                  {formik.errors.doctorRpps}
                </Typography>
              </Grid>
            </Grid>

            {/* PATIENT */}
            <Grid item xs={12}>
              <Divider>
                <PersonIcon
                  sx={{ marginBottom: "-10px", color: "#6EC1E4" }}
                />
                <Typography
                  variant="h6"
                  component="h6"
                  sx={{ color: "#6EC1E4" }}
                >
                  Patient
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Civilité*
              </Typography>
              <RadioGroup
                aria-labelledby="genderSelectionRadioButtons"
                name="patientSex"
                value={formik.values.patientSex}
                onChange={formik.handleChange}
                row
              >
                <FormControlLabel
                  value="female"
                  control={<Radio />}
                  label="Mme/Mlle"
                />
                <FormControlLabel
                  value="male"
                  control={<Radio />}
                  label="M."
                />
              </RadioGroup>
              <Typography color="error">{formik.errors.patientSex}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Nom de famille*
              </Typography>
              <TextField
                name="patientLastName"
                label="Nom de famille du patient"
                variant="outlined"
                fullWidth
                value={formik.values.patientLastName}
                onChange={formik.handleChange}
                helperText={formik.errors.patientLastName || ""}
                error={!!formik.errors.patientLastName}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Date de naissance*
              </Typography>
              <DateInput
                value={formik.values.patientBirthDate}
                onChange={(value) =>
                  formik.setFieldValue("patientBirthDate", value)
                }
                error={!!formik.errors.patientBirthDate}
                helperText={formik.errors.patientBirthDate}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Prénom*
              </Typography>
              <TextField
                name="patientFirstName"
                label="Prénom du patient"
                variant="outlined"
                fullWidth
                value={formik.values.patientFirstName}
                onChange={formik.handleChange}
                helperText={formik.errors.patientFirstName || ""}
                error={!!formik.errors.patientFirstName}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Téléphone*
              </Typography>
              <TextField
                name="patientPhone"
                label="Mobile uniquement, un code de verrification vous sera envoyé pour valider votre demande"
                variant="outlined"
                fullWidth
                value={formik.values.patientPhone}
                onChange={formik.handleChange}
                helperText={formik.errors.patientPhone || ""}
                error={!!formik.errors.patientPhone}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Téléphone de la personne de confiance
              </Typography>
              <TextField
                name="personneDeConfiancePhone"
                label="Numéro joignable d'un proche (optionnel)"
                variant="outlined"
                fullWidth
                value={formik.values.personneDeConfiancePhone ?? ""}
                onChange={formik.handleChange}
                helperText={formik.errors.personneDeConfiancePhone || ""}
                error={!!formik.errors.personneDeConfiancePhone}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Adresse
              </Typography>
              <TextField
                name="patientAddress"
                label="Adresse"
                variant="outlined"
                value={formik.values.patientAddress}
                onChange={formik.handleChange}
                fullWidth
                helperText={formik.errors.patientAddress || ""}
                error={!!formik.errors.patientAddress}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Email*
              </Typography>
              <TextField
                name="patientEmail"
                label="Adresse email valide"
                variant="outlined"
                fullWidth
                value={formik.values.patientEmail}
                onChange={formik.handleChange}
                helperText={formik.errors.patientEmail || ""}
                error={!!formik.errors.patientEmail}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                N° Sécurité Sociale*
              </Typography>
              <TextField
                name="ssn"
                label="15 chiffres"
                variant="outlined"
                value={formik.values.ssn ?? ""}
                onChange={formik.handleChange}
                fullWidth
                inputProps={{
                  maxLength: 15,
                }}
                helperText={formik.errors.ssn || ""}
                error={!!formik.errors.ssn}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Taille{" "}
                <span style={{ color: "gray", fontSize: "0.8em" }}>
                  (en cm)*
                </span>
              </Typography>
              <TextField
                type="number"
                name="patientHeight"
                label="Taille"
                variant="outlined"
                fullWidth
                value={
                  formik.values.patientHeight
                    ? parseInt(formik.values.patientHeight.toString())
                    : ""
                }
                onChange={formik.handleChange}
                error={!!formik.errors.patientHeight}
                helperText={formik.errors.patientHeight}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">cm</InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Poids{" "}
                <span style={{ color: "gray", fontSize: "0.8em" }}>
                  (en kg)*
                </span>
              </Typography>
              <TextField
                type="number"
                name="patientWeight"
                label="Poids"
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">kg</InputAdornment>
                  ),
                }}
                value={
                  formik.values.patientWeight
                    ? parseInt(formik.values.patientWeight.toString())
                    : ""
                }
                onChange={formik.handleChange}
                error={!!formik.errors.patientWeight}
                helperText={formik.errors.patientWeight}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Patient valide?*
              </Typography>
              <Select
                name="patientMobility"
                value={formik.values.patientMobility}
                fullWidth
                onChange={formik.handleChange}
                error={!!formik.errors.patientMobility}
              >
                <MenuItem value="Oui">Oui</MenuItem>
                <MenuItem value="Fauteuil roulant">Fauteuil roulant</MenuItem>
                <MenuItem value="Brancard">Brancard</MenuItem>
              </Select>
              <Typography color="error">
                {formik.errors.patientMobility}
              </Typography>
            </Grid>

            {/* PREGNANCY BLOCK (only for Mme/Mlle in childbearing age) */}
            {showPregnancyBlock && (
              <>
                <Grid item xs={12} sx={{ marginTop: "10px" }}>
                  <Divider>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ color: "#6EC1E4" }}
                    >
                      Grossesse / allaitement
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" component="h6">
                    Pensez-vous être enceinte ?
                  </Typography>
                  <RadioGroup
                    name="pregnancyStatus"
                    value={formik.values.pregnancyStatus ?? ""}
                    onChange={formik.handleChange}
                    row
                  >
                    <FormControlLabel
                      value="oui"
                      control={<Radio />}
                      label="Oui"
                    />
                    <FormControlLabel
                      value="non"
                      control={<Radio />}
                      label="Non"
                    />
                    <FormControlLabel
                      value="pas certaine"
                      control={<Radio />}
                      label="Pas certaine"
                    />
                  </RadioGroup>
                </Grid>

                {formik.values.pregnancyStatus === "oui" && (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      marginTop: "8px",
                      backgroundColor: "#fff3cd",
                      padding: "10px",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography variant="body1">
                      Seules les <b>scintigraphies pulmonaires</b> peuvent être
                      réalisées. Pour toute autre demande, veuillez contacter
                      le secrétariat au <b>03 84 96 29 80</b>.
                    </Typography>
                  </Grid>
                )}

                {formik.values.pregnancyStatus === "non" && (
                  <Grid item xs={12}>
                    <Typography variant="h6" component="h6">
                      Contraception ou stérilité (ménopause, chirurgie, autre…)
                      ?
                    </Typography>
                    <RadioGroup
                      name="contraceptionOrSterility"
                      value={formik.values.contraceptionOrSterility ?? ""}
                      onChange={formik.handleChange}
                      row
                    >
                      <FormControlLabel
                        value="oui"
                        control={<Radio />}
                        label="Oui"
                      />
                      <FormControlLabel
                        value="non"
                        control={<Radio />}
                        label="Non"
                      />
                    </RadioGroup>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="h6" component="h6">
                    Êtes-vous en période d'allaitement ?
                  </Typography>
                  <RadioGroup
                    name="breastfeeding"
                    value={formik.values.breastfeeding ?? ""}
                    onChange={formik.handleChange}
                    row
                  >
                    <FormControlLabel
                      value="oui"
                      control={<Radio />}
                      label="Oui"
                    />
                    <FormControlLabel
                      value="non"
                      control={<Radio />}
                      label="Non"
                    />
                  </RadioGroup>
                </Grid>
              </>
            )}

            {/* EXAMEN */}
            <Grid item xs={12} sx={{ marginTop: "5px" }}>
              <Divider>
                <MedicationIcon
                  sx={{ marginBottom: "-10px", color: "#6EC1E4" }}
                />
                <Typography
                  variant="h6"
                  component="h6"
                  sx={{ color: "#6EC1E4" }}
                >
                  Examen
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" component="h6">
                Choisissez votre centre d'imagerie*
              </Typography>
              <Select
                name="site"
                value={formik.values.site}
                fullWidth
                onChange={formik.handleChange}
                error={!!formik.errors.site}
              >
                {siteOptions.map((site) => (
                  <MenuItem key={site.value} value={site.value}>
                    {site.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography style={{ color: "red" }}>
                {formik.errors.site}
              </Typography>
            </Grid>

            {formik.values.site && formik.values.site !== -1 && (
              <Grid item xs={12}>
                <Typography variant="h6" component="h6">
                  Type d'examen*
                </Typography>
                <Select
                  name="examType"
                  value={formik.values.examType}
                  fullWidth
                  onChange={formik.handleChange}
                  error={!!formik.errors.examType}
                >
                  {examTypeOptions.map((examType) => (
                    <MenuItem key={examType.value} value={examType.value}>
                      {examType.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography color="error">
                  {formik.errors.examType}
                </Typography>
              </Grid>
            )}

            {formik.values.examType && (
              <Grid item xs={12}>
                <Typography variant="h6" component="h6">
                  Examen*
                </Typography>
                <Select
                  name="exam"
                  value={formik.values.exam}
                  fullWidth
                  onChange={formik.handleChange}
                  error={!!formik.errors.exam}
                >
                  {examOptions.map((ex) => {
                    const isPulmonaire = /pulmonaire/i.test(ex.label);
                    return (
                      <MenuItem
                        key={ex.value}
                        value={ex.value}
                        disabled={isPulmonaire}
                        sx={
                          isPulmonaire
                            ? { color: "gray", fontStyle: "italic" }
                            : undefined
                        }
                      >
                        {ex.label}
                        {isPulmonaire && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: "0.8rem",
                              color: "#a05a00",
                            }}
                          >
                            — Merci de faire la demande par téléphone
                          </span>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
                <Typography color="error">{formik.errors.exam}</Typography>
              </Grid>
            )}

            {formik.values.exam && (
              <>
                <Grid item xs={12}>
                  {questions?.map((_question) => (
                    <Questions
                      question={_question}
                      onAnswerChange={handleAnswerChange}
                    />
                  ))}
                </Grid>

                {/* Comments — replaces the old "Période de rendez-vous souhaitée" dropdown */}
                <Grid item xs={12} sx={{ marginTop: "8px" }}>
                  <Typography variant="h6" component="h6">
                    Commentaires
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "gray", marginBottom: "6px" }}
                  >
                    Caractère d'urgence, intervention chirurgicale prévue,
                    prochaine consultation, disponibilités, motifs…
                  </Typography>
                  <TextField
                    name="comments"
                    label="Commentaires"
                    variant="outlined"
                    multiline
                    minRows={4}
                    fullWidth
                    value={formik.values.comments}
                    onChange={formik.handleChange}
                    error={!!formik.errors.comments}
                    helperText={!!formik.errors.comments}
                  />
                </Grid>

                {/* DOCUMENTS */}
                <Grid item xs={12} sx={{ marginTop: "5px" }}>
                  <Divider>
                    <Typography
                      variant="h6"
                      component="h6"
                      sx={{ color: "#6EC1E4" }}
                    >
                      Documents
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" component="h6">
                    Ordonnance obligatoire*
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FileInput
                    files={files}
                    onChange={handleFileChange}
                    onDelete={handleFileDelete}
                  />
                </Grid>

                {errorMessage && (
                  <Grid
                    item
                    xs={12}
                    sx={{
                      marginTop: "15px",
                      backgroundColor: "#fce5e8",
                      padding: "8px",
                      position: "relative",
                    }}
                  >
                    <CloseIcon
                      style={{
                        float: "right",
                        right: 4,
                        color: "red",
                        fontSize: "18px",
                        cursor: "pointer",
                        fontWeight: "bold",
                      }}
                      onClick={() => setErrorMessage("")}
                    />
                    <Typography
                      variant="body1"
                      style={{ color: "red" }}
                      fontSize={14}
                    >
                      {errorMessage}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ marginTop: "15px" }}>
                  <Button
                    onClick={onSubmit}
                    variant="contained"
                    endIcon={
                      isSending ? (
                        <CircularProgress
                          size={20}
                          style={{ color: "darkgray" }}
                        />
                      ) : (
                        <SendIcon />
                      )
                    }
                    fullWidth
                    disabled={isSending}
                  >
                    {isSending ? "Patientez" : "Confirmer"}
                  </Button>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ScintiEstPatientForm;
